import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Mail, ArrowLeft, Phone, HelpCircle, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { mobile });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-10 bg-slate-50/40" data-testid="forgot-password-page">
      <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-8 w-full max-w-md">
        <div className="w-12 h-12 rounded-xl nc-gradient mx-auto flex items-center justify-center text-white font-bold text-xl">N</div>

        {!result && (
          <>
            <h1 className="mt-4 text-center text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
              Forgot your password?
            </h1>
            <p className="text-center text-sm text-slate-500 mt-1">
              Enter your registered mobile number — we'll send a password reset link to the email on your account.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Mobile number</label>
                <div className="relative mt-1">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    className="nc-input pl-9"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    required
                    maxLength="10"
                    pattern="[0-9]{10}"
                    inputMode="numeric"
                    data-testid="forgot-mobile-input"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="forgot-error">{error}</p>}
              <button type="submit" disabled={loading} className="nc-btn-primary w-full justify-center" data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Send reset link
              </button>
            </form>
          </>
        )}

        {result?.email_on_file && (
          <div className="mt-6 text-center" data-testid="forgot-success">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h2 className="mt-3 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Check your email</h2>
            <p className="mt-2 text-sm text-slate-600">
              If an account exists for that number, a reset link has been sent to{" "}
              <strong className="text-slate-900">{result.email_masked}</strong>.
            </p>
            <p className="mt-3 text-xs text-slate-500">
              The link expires in 30 minutes. Don't see it? Check your spam folder.
            </p>
          </div>
        )}

        {result && !result.email_on_file && (
          <div className="mt-6" data-testid="forgot-no-email">
            <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 mx-auto flex items-center justify-center">
              <HelpCircle className="w-6 h-6" />
            </div>
            <h2 className="mt-3 text-center text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
              We can't reset your password automatically
            </h2>
            <p className="mt-3 text-sm text-slate-600 text-center">
              We don't have an email address on file for this mobile number, so we can't send a reset link.
              Please contact our customer care team and we'll help you regain access.
            </p>
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                <a href="mailto:support@nakshacart.com" className="text-emerald-700 font-medium">support@nakshacart.com</a>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Mention your registered mobile number so we can verify your account quickly.
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-slate-600">
          <Link to="/login" className="inline-flex items-center gap-1 text-slate-600 hover:text-emerald-700" data-testid="forgot-back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
